import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import type { EmbedManageResponse } from '@wrapforce/shared';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useEmbedWidget } from './api';
import { ProviderLogo } from './ProviderLogo';

const STATUS_CLASS: Record<EmbedManageResponse['status'], string> = {
  active: 'bg-success/15 text-success',
  paused: 'bg-warning/15 text-warning',
  pending: 'bg-surface-2 text-muted',
  disabled: 'bg-danger/15 text-danger',
};

export function QuoteWidgetCard({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('integrations');
  const navigate = useNavigate();
  const { data, isLoading } = useEmbedWidget(workspaceId);

  return (
    <Card
      role="button"
      tabIndex={0}
      data-tour="integration-quote-widget"
      className="cursor-pointer transition-colors hover:border-accent focus-visible:border-accent focus-visible:outline-none"
      onClick={() => navigate('/integrations/quote-widget')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/integrations/quote-widget');
        }
      }}
    >
      <CardHeader>
        <ProviderLogo provider="quote_widget" />
        <CardTitle>{t('quoteWidget.title')}</CardTitle>
        <ChevronRight size={16} className="ml-auto text-muted" />
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted">{t('quoteWidget.cardDescription')}</p>
        {isLoading ? null : data?.widget ? (
          <span className={`shrink-0 px-2.5 py-1 text-xs font-bold uppercase ${STATUS_CLASS[data.widget.status]}`}>
            {t(`quoteWidget.status.${data.widget.status}`)}
          </span>
        ) : (
          <span className="shrink-0 bg-surface-2 px-2.5 py-1 text-xs font-bold text-muted">{t('quoteWidget.notSetUp')}</span>
        )}
      </CardContent>
    </Card>
  );
}
